import * as React from 'react';
import { observer } from 'mobx-react-lite';
import SmartDateSelector from 'owa-calendar-compose-form-view/lib/components/SmartDateSelector';
import { Icon } from '@fluentui/react/lib/Icon';
import { isFeatureEnabled } from 'owa-feature-flags';
import TimeSuggestionsHost from 'owa-calendar-meeting-forms-common-view/lib/components/TimeSuggestionsHost';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import shouldShowTimeSuggestions from 'owa-calendar-compose-form-store/lib/selectors/shouldShowTimeSuggestions';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { logQuickComposeControlUsed, updateDateTimes } from 'owa-calendar-compose-form-actions';
import { owaDate, OwaDate } from 'owa-datetime';
import { QuickComposeControl } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import { ControlIcons } from 'owa-control-icons';
import { TimeChangeActionType } from 'owa-calendar-helpers-types/lib/TimeChangeActionType';
import { findMeetingTimesConstants } from 'owa-calendar-time-suggestions/lib/constants';
import { hasAttendees } from 'owa-calendar-event-capabilities';
import { getQuickComposeViewState } from 'owa-calendar-compose-store-manager';
import { lazyRenderSpeedyMeetingsTeachingUI } from 'owa-calendar-compose-form-utils';

import expStyles from './TimeComponent.scss';
import styles from './QuickComposeContent.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface TimeComponentProps {
    formId: string;
    isInEditMode: boolean;
    isTimePanelViewType: boolean;
    onSizeUpdate?: () => void;
    onTimeSuggestionNotFound: () => void;
    colorScheme: ColorScheme;
    event: CalendarEvent;
}

interface ExperimentProps extends TimeComponentProps {
    isExp: boolean;
    renderAfterDateRow?: (
        timePickerRefTarget: HTMLElement | React.RefObject<Element>
    ) => JSX.Element;
    showTimeSuggestions?: boolean;
}

/* This class will be removed after TimeSuggestionsExperiments are done */
export default observer(function TimeComponent(props: TimeComponentProps) {
    const { formId, isInEditMode } = props;
    const isExp =
        isFeatureEnabled('cal-mf-timeSuggestions-exp-a') ||
        isFeatureEnabled('cal-mf-timeAccommodations');
    const isAllDayEvent = props.event?.IsAllDayEvent;
    const showTimeSuggestions =
        shouldShowTimeSuggestions(formId) && !isInEditMode && !isAllDayEvent;
    const quickComposeViewState = getQuickComposeViewState(formId);
    const renderSpeedyMeetingsTeachingUI = lazyRenderSpeedyMeetingsTeachingUI.tryImportForRender();
    const renderAfterDateRow = renderSpeedyMeetingsTeachingUI
        ? target =>
              renderSpeedyMeetingsTeachingUI(
                  target,
                  quickComposeViewState.shouldAdjustStartTimeEndTime
              )
        : undefined;

    /** Splitting this logic instead of using flex-direction: column-reverse for accessibility reasons
     * There's a keyboard navigating disconnect between the DOM and flex visual presentation when using row-reverse and column-reverse
     * https://tink.uk/flexbox-the-keyboard-navigation-disconnect/ */
    if (isExp) {
        return (
            <div>
                {showTimeSuggestions && <TimeSuggestionHost {...props} isExp={isExp} />}
                {
                    <DateTimeRow
                        {...props}
                        isExp={isExp}
                        renderAfterDateRow={renderAfterDateRow}
                        showTimeSuggestions={showTimeSuggestions}
                    />
                }
            </div>
        );
    } else {
        return (
            <div>
                {
                    <DateTimeRow
                        {...props}
                        isExp={isExp}
                        renderAfterDateRow={renderAfterDateRow}
                        showTimeSuggestions={showTimeSuggestions}
                    />
                }
                {showTimeSuggestions && <TimeSuggestionHost {...props} isExp={isExp} />}
            </div>
        );
    }
});

function DateTimeRow(props: ExperimentProps) {
    const {
        formId,
        isInEditMode,
        isTimePanelViewType,
        onSizeUpdate,
        isExp,
        event,
        renderAfterDateRow,
        showTimeSuggestions,
    } = props;
    const timeSuggestionHostDisplayed = showTimeSuggestions && isAllDayAndHasAttendees(event);

    const onDateTimesChanged = () => {
        logQuickComposeControlUsed(formId, QuickComposeControl.DateTime, true /* isFocus*/);
    };

    return (
        <div
            className={classNames(
                styles.composeRow,
                styles.dateTimeContainer,
                isExp && timeSuggestionHostDisplayed ? expStyles.composeRowExp : null
            )}>
            <Icon iconName={ControlIcons.Clock} className={classNames(styles.clockRowIcon)} />
            <SmartDateSelector
                isNarrow={isInEditMode}
                timeChangeActionType={TimeChangeActionType.QuickComposeTimeControls}
                onSizeUpdate={onSizeUpdate}
                onDateTimesChanged={onDateTimesChanged}
                datePickerClassName={classNames(styles.datePickerStyle, {
                    isTimePanelViewType: isTimePanelViewType,
                })}
                isTimePanelViewType={isTimePanelViewType}
                dateRowClassName={isTimePanelViewType ? styles.timePanelDateRow : null}
                startEndBlockClassName={isTimePanelViewType ? styles.startEndBlock : null}
                toTextClassName={isTimePanelViewType ? styles.toText : null}
                timeAndDurationColumnClassName={
                    isTimePanelViewType ? styles.timeAndDurationColumn : null
                }
                timePickerClassName={isTimePanelViewType ? styles.timePickerForTimePanel : null}
                durationDropDownClassName={
                    isTimePanelViewType ? styles.durationDropDownForTimePanel : null
                }
                timePickerMultilineClassName={
                    isTimePanelViewType ? styles.timePickerTimePanelMultiline : null
                }
                renderAfterDateRow={renderAfterDateRow}
                formId={formId}
            />
        </div>
    );
}

export const TimeSuggestionHost = React.forwardRef(function TimeSuggestionHost(
    props: ExperimentProps
) {
    const {
        formId,
        colorScheme,
        event,
        onTimeSuggestionNotFound,
        isExp,
        isTimePanelViewType,
    } = props;

    /**
     * Callback handler when time suggestion is clicked
     */
    const onTimeSuggestionClicked = (start: OwaDate, end: OwaDate) => {
        // Update start/end to match the timezone of start end in the compose form
        // Otherwise, the start/end timezones will change when the suggestion is clicked
        start = owaDate(event.StartTimeZoneId, start);
        end = owaDate(event.EndTimeZoneId, end);
        updateDateTimes(formId, { start: start, end: end }, TimeChangeActionType.TimeSuggestions);
    };

    return (
        <TimeSuggestionsHost
            className={isExp ? expStyles.timeSuggestionsExp : styles.timeSuggestions}
            colorScheme={colorScheme}
            event={event}
            maxCandidates={
                isTimePanelViewType
                    ? findMeetingTimesConstants.MAX_CANDIDATES_TIMEPANEL
                    : findMeetingTimesConstants.MAX_CANDIDATES_QUICK_COMPOSE
            }
            onTimeSuggestionClicked={onTimeSuggestionClicked}
            onTimeSuggestionNotFound={onTimeSuggestionNotFound}
            viewStateId={formId}
        />
    );
});

function isAllDayAndHasAttendees(event: CalendarEvent) {
    return !event.IsAllDayEvent && hasAttendees(event, true /* ignoreResources */);
}
